import React from 'react'
import clsx from 'clsx'
import { resolveReportGeofence } from '../../../map/helpers/reportGeofence'
import { MapTrifold, X } from '@phosphor-icons/react'
import { Box, Stack, Typography } from '@mui/material'
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined'
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined'
import SellOutlinedIcon from '@mui/icons-material/SellOutlined'
import { Modal as BootstrapModal } from 'react-bootstrap'
import ActivityMapMobileModalShell from '../../../../components/mobile/ActivityMapMobileModalShell'
import { ActivityMapMobileLoadingState } from '../../../../components/mobile'
import ShowMapModalContent from '../../../map/ShowMapModalContent'
import dayjs from '../../../../utils/dayjsConfig'
import { clearCoordinates, effectiveLocationStr } from '../../../../helpers/utils'
import ActivityReportMapDetails from './ActivityReportMapDetails'
import ActivityReportMapLegend from './ActivityReportMapLegend'
import ReportMapSection from './ReportMapSection'
import { ACTIVITY_REPORT_MAP_COLORS, ACTIVITY_REPORT_MAP_TEXT } from './constants'

const DETAIL_ITEM_ICONS = {
  activity: <SellOutlinedIcon fontSize="small" />,
  dateTime: <CalendarMonthOutlinedIcon fontSize="small" />,
  effectiveLocation: <PlaceOutlinedIcon fontSize="small" />
}

const getDateTimeParts = (createdDate) => {
  const selectedCreatedDate = createdDate ? dayjs(createdDate) : null

  if (!selectedCreatedDate?.isValid()) {
    return {
      date: '',
      time: '',
      dateTime: ''
    }
  }

  const date = selectedCreatedDate.format('L')
  const time = selectedCreatedDate.format('LT')

  return {
    date,
    time,
    dateTime: date && time ? `${date} - ${time}` : date || time || ''
  }
}

export default function ActivityReportMapModal({
  show,
  onHide,
  isMobileLayout,
  salesmanName,
  activity,
  formatActivityName,
  shouldRenderMobileMap
}) {
  const activityDateTime = activity?.created_date || activity?.datetime || activity?.date_time
  const createdDate = getDateTimeParts(activityDateTime)
  const activityName = activity
    ? formatActivityName?.(activity.activity_name) || activity.activity_name || ''
    : ''
  const geofenceInfo = resolveReportGeofence(activity || {})
  const legendGeofenceDistance = geofenceInfo.radius
  const effectiveLocation =
    activity && !activity.activity_name?.includes('LIQUID')
      ? effectiveLocationStr(
          activity.effective_location,
          activity.client_latitude,
          activity.latitude
        )
      : ACTIVITY_REPORT_MAP_TEXT.emptyValue
  const hasClientMapPoint =
    Number.parseFloat(activity?.client_latitude ?? 0) !== 0 &&
    Number.parseFloat(activity?.client_longitude ?? 0) !== 0
  const latitude = clearCoordinates(activity?.latitude)
  const longitude = clearCoordinates(activity?.longitude)

  const detailItems = [
    {
      key: 'activity',
      icon: DETAIL_ITEM_ICONS.activity,
      label: ACTIVITY_REPORT_MAP_TEXT.activityLabel,
      value: activityName || ACTIVITY_REPORT_MAP_TEXT.emptyValue
    },
    {
      key: 'dateTime',
      icon: DETAIL_ITEM_ICONS.dateTime,
      label: ACTIVITY_REPORT_MAP_TEXT.dateTimeLabel,
      value: createdDate.dateTime || ACTIVITY_REPORT_MAP_TEXT.emptyValue
    },
    {
      key: 'effectiveLocation',
      icon: DETAIL_ITEM_ICONS.effectiveLocation,
      label: ACTIVITY_REPORT_MAP_TEXT.geofenceStatusLabel,
      value: (
        <span
          className={clsx('activity-report-geofence-status', {
            'activity-report-geofence-status--inside': effectiveLocation === 'Sí',
            'activity-report-geofence-status--outside': effectiveLocation === 'No',
            'activity-report-geofence-status--unknown':
              effectiveLocation !== 'Sí' && effectiveLocation !== 'No'
          })}
        >
          {effectiveLocation}
        </span>
      )
    }
  ]

  const legendItems = [
    {
      key: 'activity',
      label: ACTIVITY_REPORT_MAP_TEXT.activityLabel,
      color: 'red',
      pinColor: ACTIVITY_REPORT_MAP_COLORS.activityLegend
    },
    ...(hasClientMapPoint
      ? [
          {
            key: 'client',
            label: ACTIVITY_REPORT_MAP_TEXT.clientLabel,
            color: 'green',
            pinColor: ACTIVITY_REPORT_MAP_COLORS.clientLegend
          }
        ]
      : []),
    ...(hasClientMapPoint && legendGeofenceDistance
      ? [
          {
            key: 'geofence',
            label: ACTIVITY_REPORT_MAP_TEXT.geofenceLegendLabel(legendGeofenceDistance),
            color: 'geofence'
          }
        ]
      : [])
  ]

  const renderMapContent = () => {
    if (!activity) return null

    if (isMobileLayout && !shouldRenderMobileMap) {
      return <ActivityMapMobileLoadingState />
    }

    return (
      <ShowMapModalContent
        modern
        geofenceInfo={geofenceInfo}
        geofenceStatus={
          effectiveLocation === 'Sí' ? 'inside' : effectiveLocation === 'No' ? 'outside' : 'unknown'
        }
        longitude={longitude}
        latitude={latitude}
        clientLatitude={activity.client_latitude}
        clientLongitude={activity.client_longitude}
        isMobileView={isMobileLayout}
        salesmanVisitData={activity}
        recordedGeoMaxDistanceInMeter={legendGeofenceDistance}
        hasAllowedLocation={activity.has_allowed_location}
        errorCoordinatesCode={activity.error_coordinates_code}
      />
    )
  }

  return (
    <ActivityMapMobileModalShell
      show={show && Boolean(activity)}
      onHide={onHide}
      isMobileLayout={isMobileLayout}
      ariaLabelledBy="activity-report-map-modal-title"
      desktopDialogClassName="modal-90w activity-report-modern"
      modern
      mobileEyebrow={ACTIVITY_REPORT_MAP_TEXT.mobileEyebrow}
      mobileTitle={
        <Typography className="activity-map-mobile-title">
          {`${ACTIVITY_REPORT_MAP_TEXT.reportPrefix} ${salesmanName}`}
        </Typography>
      }
      mobileBody={
        <Stack spacing={1.5} className="activity-map-mobile-layout">
          <ActivityReportMapDetails
            clientName={activity?.client_name}
            overline={ACTIVITY_REPORT_MAP_TEXT.detailsSectionTitle}
            items={detailItems}
            isMobileLayout
          />

          <Box className="activity-map-mobile-map-card">
            <Box className="activity-map-mobile-map-card-head">
              <Box>
                <Typography className="activity-map-mobile-map-title">
                  {ACTIVITY_REPORT_MAP_TEXT.mapSectionTitle}
                </Typography>
                <Typography className="activity-map-mobile-map-subtitle">
                  {ACTIVITY_REPORT_MAP_TEXT.mapSectionSubtitle}
                </Typography>
              </Box>
              <ActivityReportMapLegend items={legendItems} isMobileLayout />
            </Box>

            <div id="map-canvas-mobile" className="activity-map-mobile-canvas">
              {renderMapContent()}
            </div>
          </Box>
        </Stack>
      }
      desktopChildren={
        <>
          <BootstrapModal.Header className="no-border-bottom activity-report-modern__header">
            <div className="activity-report-modern__hero-icon">
              <MapTrifold size={28} />
            </div>
            <BootstrapModal.Title id="activity-report-map-modal-title">
              <span className="activity-report-modern__title">{`${ACTIVITY_REPORT_MAP_TEXT.reportPrefix} ${salesmanName}`}</span>
            </BootstrapModal.Title>
            <button
              className="activity-report-modern__close"
              type="button"
              onClick={onHide}
              aria-label="Cerrar reporte de ubicación"
            >
              <X size={20} />
            </button>
          </BootstrapModal.Header>
          <BootstrapModal.Body>
            <Stack spacing={2.5}>
              <ActivityReportMapDetails
                clientName={activity?.client_name}
                overline={ACTIVITY_REPORT_MAP_TEXT.detailsSectionTitle}
                items={detailItems}
              />

              <ReportMapSection legendItems={legendItems}>{renderMapContent()}</ReportMapSection>
            </Stack>
          </BootstrapModal.Body>
        </>
      }
    />
  )
}
